import { ErrorMessage } from '@hookform/error-message';
import React, { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ErrorMessageText from '../ErrorMessageText';
import HintText from '../HintText';
import Label from '../Label';
import { SFormGroup } from './styles';

export interface TextInputProps {
    name: string;
    label?: string;
    validate?(value: string): boolean | string;
    disabled?: boolean;
    hint?: string;
    required?: boolean;
    placeholder?: string;
    errorMessage?: string;
    type?: 'text' | 'password' | 'email' | 'number';
    pattern?: { value: RegExp; message: string };
    semi?: boolean;
    isTextArea?: boolean;
    min?: number;
    max?: number;
    helper?: string;
    transform?: (value: string) => string;
}

const TextInput: FC<TextInputProps> = ({
    name,
    label,
    hint,
    type = 'text',
    disabled = false,
    placeholder,
    required = false,
    pattern,
    validate,
    helper,
    semi = false,
    isTextArea = false,
    transform,
    ...rest
}) => {
    const { t } = useTranslation();
    const {
        formState: { errors },
        register,
    } = useFormContext();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        if (transform) {
            const transformedValue = transform(value);
            e.target.value = transformedValue;
        }
    };

    return (
        <SFormGroup semi={semi} withError={!!errors[name]}>
            {label && <Label helper={helper} name={name} required={required} text={label} />}
            {isTextArea ? (
                <textarea
                    {...register(name, {
                        pattern,
                        required: required ? `${t('common.required-field')}` : false,
                        validate,
                    })}
                    disabled={disabled}
                    rows={5}
                    {...(placeholder && { placeholder: t(placeholder) })}
                />
            ) : (
                <input
                    {...register(name, {
                        pattern,
                        required: required ? `${t('common.required-field')}` : false,
                        validate,
                    })}
                    disabled={disabled}
                    {...(placeholder && { placeholder: t(placeholder) })}
                    onChange={handleChange}
                    type={type}
                    {...rest}
                />
            )}
            {hint && <HintText text={hint} />}
            {errors[name] && (
                <ErrorMessage
                    errors={errors}
                    name={name}
                    render={({ message }) => <ErrorMessageText text={message} />}
                />
            )}
        </SFormGroup>
    );
};

export default TextInput;
